import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Toggle } from '@/components/ui/Toggle';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { useCompanyInfo, useUpdateOpenClawConfig } from '@/hooks/useSettings';
import { Webhook, AlertCircle } from 'lucide-react';

interface FormValues {
  webhookUrl: string;
  apiKey: string;
  enabled: boolean;
}

export function OpenClawConfigForm() {
  const { data: company, isLoading } = useCompanyInfo();
  const update = useUpdateOpenClawConfig();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      webhookUrl: '',
      apiKey: '',
      enabled: false,
    },
  });

  useEffect(() => {
    if (company) {
      reset({
        webhookUrl: company.openclaw?.webhookUrl ?? '',
        apiKey: company.openclaw?.apiKey ?? '',
        enabled: company.openclaw?.enabled ?? false,
      });
    }
  }, [company, reset]);

  const onSubmit = async (values: FormValues) => {
    await update.mutateAsync({
      webhookUrl: values.webhookUrl.trim(),
      apiKey: values.apiKey.trim() || undefined,
      enabled: values.enabled,
    });
  };

  const enabled = watch('enabled');
  const webhookUrl = watch('webhookUrl');

  return (
    <Card>
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded bg-secondary/10 text-secondary flex items-center justify-center flex-shrink-0">
          <Webhook size={18} />
        </div>
        <div>
          <CardTitle>OpenClaw WhatsApp integration</CardTitle>
          <CardDescription>
            Setiap invoice baru otomatis dikirim ke customer via WhatsApp.
          </CardDescription>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <Chip variant={enabled ? 'success' : 'default'}>
          {enabled ? 'Aktif' : 'Nonaktif'}
        </Chip>
        <span className="text-caption text-ink-subtle">
          {enabled
            ? 'Invoice baru akan dikirim via WhatsApp'
            : 'Invoice tidak akan dikirim otomatis'}
        </span>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Toggle
          checked={enabled}
          onChange={(v) => setValue('enabled', v, { shouldDirty: true })}
          label="Aktifkan OpenClaw"
          description="Hanya aktifkan setelah webhook URL kamu siap menerima."
        />

        <Input
          label="Webhook URL"
          type="url"
          placeholder="https://api.openclaw.io/webhook/..."
          hint="POST endpoint dari OpenClaw yang menerima event invoice.created."
          error={errors.webhookUrl?.message}
          {...register('webhookUrl', {
            validate: (v) => {
              if (enabled && !v.trim()) return 'Webhook URL wajib diisi kalau aktif';
              if (v.trim() && !v.startsWith('https://')) return 'Harus https://';
              return true;
            },
          })}
          disabled={isLoading}
        />

        <Input
          label="API Key (opsional)"
          type="password"
          placeholder="Bearer token dari OpenClaw"
          hint="Disimpan terenkripsi di Firestore. Dikirim sebagai Authorization header."
          {...register('apiKey')}
        />

        {enabled && !webhookUrl && (
          <div className="flex items-start gap-2 p-3 rounded bg-warning/5 border border-warning/30 text-small text-warning">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>Webhook URL belum diisi. Invoice tidak akan terkirim sampai ini diisi.</span>
          </div>
        )}

        <div className="pt-2 flex justify-end">
          <Button
            type="submit"
            variant="primary"
            loading={update.isPending}
            disabled={!isDirty}
          >
            Simpan konfigurasi
          </Button>
        </div>
      </form>
    </Card>
  );
}
